import { useState } from 'react';
import { ChevronRight, Copy, FileX, HardDrive } from 'lucide-react';
import { formatBytes, formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';
import { Confirm } from '@/components/Confirm';

/**
 * What the detectors flagged, grouped by what they found.
 *
 * The register is one row per flag, and on a real bucket that is a hundred
 * rows repeating one identical sentence, keyed by a content hash. An
 * operator cannot read that, and does not need to: the decision is the same
 * for every flag of a kind, so the kind is the unit. Each kind states its
 * total cost in files and bytes, its reason once, and how much of it is
 * decided.
 *
 * The individual flags remain reachable - the evidence has to be inspectable
 * to be trusted - but folded away, because opening them is a deliberate act
 * rather than the default reading experience.
 */

export interface Flag {
  id: string;
  kind: string;
  pathSummary: string;
  files: number;
  bytes: number;
  reason: string;
  proposal: string;
  decision: string | null;
  /**
   * Who proposed this, and it is not decoration.
   *
   * The contract enumerates DETERMINISTIC and AI_PROPOSED and says plainly
   * that "Deterministic output is never attributed to AI. A client may present
   * the two differently but must not merge them." This client merged them: a
   * byte-identical duplicate proven by SHA-256 and a model's suggestion were
   * the same row in the same ink, and the only destructive control on the page
   * acted on both alike.
   */
  basis: string;
}

const KIND: Record<string, { label: string; icon: typeof Copy; what: string }> = {
  DUPLICATE_CONTENT: {
    label: 'Duplicate copies',
    icon: Copy,
    what: 'the same bytes stored more than once',
  },
  OS_METADATA: {
    label: 'Operating-system files',
    icon: HardDrive,
    what: 'files the operating system wrote alongside the export',
  },
  ZERO_BYTE: { label: 'Empty files', icon: FileX, what: 'files with no content at all' },
};

/**
 * What a flag was decided, from either shape the field takes.
 *
 * The backend sends an object - `{ decision: "KEEP", decidedAt }` - and this
 * read `typeof i.decision === 'string'`, so every decided flag fell through to
 * `null` and every one of them read as still to decide. On aiinsurance26 all
 * 101 flags were decided KEEP and these rows offered "100 to decide" and
 * "1 to decide", with the bulk keep and the bulk drop live beside them, while
 * the panel heading directly above said "0 still to decide" and a green banner
 * said every flag was decided. The heading reads the raw payload and was
 * right; this reader was the wrong one.
 *
 * The contract now describes it: `CurationItemView.decision` is a nullable
 * `CurationDecisionView`, so the object IS the shape and this is no longer a
 * guess between two candidates. The string branch stays because it costs one
 * line and this is the exact field that already cost a release; an
 * unrecognised shape still reads as undecided, which asks rather than claiming
 * a ruling nobody made.
 */
function decisionOf(raw: unknown): string | null {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const inner = (raw as { decision?: unknown }).decision;
    if (typeof inner === 'string') return inner;
  }
  return null;
}

export function readFlags(value: unknown): Flag[] | null {
  const items = (value as { items?: unknown[] } | null)?.items;
  if (!Array.isArray(items)) return null;
  return items
    .filter((i): i is Record<string, unknown> => Boolean(i) && typeof i === 'object')
    .map((i) => ({
      id: String(i.id ?? ''),
      kind: String(i.kind ?? 'UNKNOWN'),
      pathSummary: String(i.pathSummary ?? ''),
      files: typeof i.files === 'number' ? i.files : 0,
      bytes: typeof i.bytes === 'number' ? i.bytes : 0,
      reason: String(i.reason ?? ''),
      proposal: String(i.proposal ?? ''),
      decision: decisionOf(i.decision),
      basis: typeof i.basis === 'string' ? i.basis : 'DETERMINISTIC',
    }));
}

export function CurationFlags({
  flags,
  onDecide,
  onDecideMany,
  busy = false,
  busyId,
}: {
  flags: Flag[];
  /** Records one flag's decision. Without it these rows are read-only, which
   *  is how an operator ended up with only "keep all 102" and no way to remove
   *  anything they did not want. */
  onDecide?: (flagId: string, decision: 'KEEP' | 'DROP') => void;
  /** Decide a whole set. The parent runs these one at a time. */
  onDecideMany?: (flagIds: string[], decision: 'KEEP' | 'DROP') => void;
  /** A write is already running, so every control here waits for it. */
  busy?: boolean;
  busyId?: string | null;
}) {
  const [open, setOpen] = useState<string | null>(null);

  if (flags.length === 0)
    return <p className="px-4 py-3 text-small text-ink-3">The detectors flagged nothing in this package.</p>;

  const kinds = [...new Set(flags.map((f) => f.kind))].map((kind) => {
    const mine = flags.filter((f) => f.kind === kind);
    return {
      kind,
      flags: mine,
      files: mine.reduce((n, f) => n + f.files, 0),
      bytes: mine.reduce((n, f) => n + f.bytes, 0),
      undecided: mine.filter((f) => !f.decision).length,
      // What a bulk drop actually removes from the buyer's package. The
      // confirm counted FLAGS, so a kind holding 12 flags over 4,318 files
      // offered "Drop 12 files" on the only destructive control on the page.
      undecidedFiles: mine.filter((f) => !f.decision).reduce((n, f) => n + f.files, 0),
      undecidedBytes: mine.filter((f) => !f.decision).reduce((n, f) => n + f.bytes, 0),
      kept: mine.filter((f) => f.decision === 'KEEP').length,
      dropped: mine.filter((f) => f.decision === 'DROP').length,
      reason: mine[0]?.reason ?? '',
      /** Distinct bases within this kind. More than one is worth showing. */
      bases: [...new Set(mine.map((f) => f.basis))],
      proposedByAi: mine.filter((f) => f.basis === 'AI_PROPOSED').length,
    };
  });

  return (
    <div>
      {kinds.map((k) => {
        const meta = KIND[k.kind] ?? { label: say(k.kind), icon: Copy, what: '' };
        const Icon = meta.icon;
        const expanded = open === k.kind;
        return (
          <div key={k.kind} className="border-b border-line last:border-0">
            <button
              aria-expanded={expanded}
              onClick={() => setOpen(expanded ? null : k.kind)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-fast hover:bg-hov"
            >
              <Icon className="h-4 w-4 flex-none text-ink-3" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="text-row text-ink">{meta.label}</span>
                  {/* "Deterministic output is never attributed to AI. A client
                      may present the two differently but must not merge them."
                      A SHA-256 duplicate and a model's suggestion were one row
                      in one ink, under one bulk drop. */}
                  {k.proposedByAi > 0 && (
                    <span className="rounded border border-warn px-1.5 py-0.5 text-caption text-warn">
                      {k.bases.length > 1
                        ? `${formatCount(k.proposedByAi)} of these proposed by AI`
                        : 'proposed by AI'}
                    </span>
                  )}
                </span>
                {/* This is the sentence describing what is about to be
                    dropped from the package in bulk. It was set in the
                    faintest ink on the row, quieter than the byte count. */}
                <span className="block text-caption text-ink-2">{meta.what}</span>
              </span>
              <span className="mono flex-none text-right text-caption text-ink-2">
                {k.files.toLocaleString()} files
              </span>
              <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(k.bytes)}</span>
              <span
                className={cn(
                  'w-28 flex-none text-right text-caption',
                  k.undecided > 0 ? 'text-warn' : k.dropped > 0 ? 'text-ink-3' : 'text-ok',
                )}
              >
                {k.undecided > 0
                  ? `${k.undecided} to decide`
                  : k.dropped > 0
                    ? `${k.dropped} dropped`
                    : 'all kept'}
              </span>
              <ChevronRight
                className={cn('h-3.5 w-3.5 flex-none text-ink-4 transition-transform', expanded && 'rotate-90')}
                aria-hidden="true"
              />
            </button>

            <div className="flex flex-wrap items-center gap-2 px-4 pb-2.5">
              <span className="min-w-0 flex-1 text-caption text-ink-3">{k.reason}</span>
              {/* These used to fire one write per flag at once through a
                  synchronous forEach over an async handler, and looked
                  identical to each other. Dropping removes files from what
                  ships, so it is the graver of the two and is drawn that
                  way. */}
              {onDecideMany && k.undecided > 0 && (
                <>
                  <button
                    disabled={busy}
                    onClick={() => onDecideMany(k.flags.filter((f) => !f.decision).map((f) => f.id), 'KEEP')}
                    className="rounded border border-line px-2 py-0.5 text-caption text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ok disabled:opacity-50"
                  >
                    keep these {formatCount(k.undecidedFiles)} files
                  </button>
                  <Confirm
                    label={`leave ${formatCount(k.undecidedFiles)} files out`}
                    confirmLabel={`Drop ${formatCount(k.undecidedFiles)} files`}
                    consequence={`${formatCount(k.undecidedFiles)} files, ${formatBytes(k.undecidedBytes)}, across ${k.undecided} ${k.undecided === 1 ? 'flag' : 'flags'}. Dropped files are recorded as excluded and do not reach the buyer.`}
                    disabled={busy}
                    onConfirm={() => onDecideMany(k.flags.filter((f) => !f.decision).map((f) => f.id), 'DROP')}
                  />
                </>
              )}
            </div>

            {expanded && (
              <div className="border-t border-line bg-panel-2">
                {k.flags.slice(0, 50).map((f) => (
                  <div
                    key={f.id}
                    className="flex items-baseline gap-3 border-b border-line px-4 py-1.5 text-caption last:border-0"
                  >
                    <span className="min-w-0 flex-1 truncate text-ink-2">{f.pathSummary}</span>
                    <span className="mono flex-none text-ink-3">{f.files} files</span>
                    <span className="mono w-16 flex-none text-right text-ink-3">{formatBytes(f.bytes)}</span>
                    {onDecide ? (
                      <span className="flex w-40 flex-none items-center justify-end gap-1">
                        {f.decision ? (
                          <span className={cn('text-caption', f.decision === 'DROP' ? 'text-warn' : 'text-ok')}>
                            {f.decision === 'DROP' ? 'left out' : 'kept'}
                          </span>
                        ) : null}
                        <button
                          disabled={busyId === f.id}
                          onClick={() => onDecide(f.id, 'KEEP')}
                          className="rounded border border-line px-1.5 py-0.5 text-caption text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ok disabled:opacity-50"
                        >
                          keep
                        </button>
                        <button
                          disabled={busyId === f.id}
                          onClick={() => onDecide(f.id, 'DROP')}
                          className="rounded border border-line px-1.5 py-0.5 text-caption text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-warn disabled:opacity-50"
                        >
                          leave out
                        </button>
                      </span>
                    ) : (
                      <span className="w-20 flex-none text-right text-ink-4">
                        {f.decision ? f.decision.toLowerCase() : 'undecided'}
                      </span>
                    )}
                  </div>
                ))}
                {k.flags.length > 50 && (
                  <p className="px-4 py-2 text-caption text-ink-4">
                    Showing 50 of {k.flags.length}. Every one carries the same reason and the same decision.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
